import { buildPickerOptions } from "./hierarchy-picker";

// The E data charts have exactly two dimensions. "bu" is the store-side key
// carried over from the original single-dimension slot UI; the API validator
// spells the same thing "businessUnit", so every crossing of that boundary
// goes through apiGroupBy below.
const OTHER_BY_DIMENSION = { bu: "country", country: "bu" };

const PICKER_HIERARCHY_BY_DIMENSION = {
  bu: "businessUnit",
  country: "country",
};

// Mirror of the API's per-list validation bound on /e-charts entityIds and
// within.entityIds. A request above it is a guaranteed 400, so the UI refuses
// to send one and explains the boundary instead (Codex round 3 finding 3).
export const E_CHARTS_MAX_ENTITY_IDS = 100;

export const isDimension = (value) => value === "bu" || value === "country";

export const otherDimension = (dimension) =>
  OTHER_BY_DIMENSION[dimension] ?? null;

export const apiGroupBy = (dimension) =>
  dimension === "bu" ? "businessUnit" : "country";

// User-facing vocabulary per dimension, kept in one place so the pickers, the
// chart titles, the breadcrumb, and the empty-state sentences cannot drift
// apart. Values are i18n keys as well as their own English fallback, matching
// the rest of this feature (R09 item 5 brief 3.2 item 15: new strings are
// hardcoded English; locale catalogs stay the known platform-wide P3).
export const DIMENSION_LABELS = {
  bu: {
    heading: "Business Units",
    all: "All Reporting Entities",
    search: "Search Reporting Entities...",
    perView: "Per BU",
    one: "business unit",
    many: "business units",
    manyCapitalized: "Business units",
  },
  country: {
    heading: "Countries",
    all: "All Countries",
    search: "Search Countries...",
    perView: "Per Country",
    one: "country",
    many: "countries",
    manyCapitalized: "Countries",
  },
};

export const dimensionLabels = (dimension) =>
  DIMENSION_LABELS[dimension] ?? DIMENSION_LABELS.bu;

export const buildDimensionOptions = (orgTree, dimension) =>
  buildPickerOptions(orgTree, PICKER_HIERARCHY_BY_DIMENSION[dimension]);

// Both option vocabularies in one shape. The refetch reaction watches this so
// a default-All first load cannot short-circuit on an empty option list and
// then sit blank once the org tree arrives (brief 3.2 item 3).
export const buildDimensionOptionIds = (orgTree) => ({
  bu: buildDimensionOptions(orgTree, "bu").map((option) => String(option.id)),
  country: buildDimensionOptions(orgTree, "country").map((option) =>
    String(option.id),
  ),
});

// Picker country ids are synthetic "country:<key>" values whose suffix is the
// raw org.country field, which is seeded from Country.key; the backend matches
// Country.key exactly and lowercase. Stripping the prefix therefore yields the
// key itself, and the lowercase guards a tenant whose stored casing drifted.
// BU ids are already the organization-unit _id the API expects.
export const normalizeEntityId = (dimension, id) => {
  const value = typeof id === "string" ? id : String(id);
  if (dimension !== "country") return value;
  const bare = value.startsWith("country:")
    ? value.slice("country:".length)
    : value;
  return bare.toLowerCase();
};

export const normalizeEntityIds = (dimension, ids = []) =>
  (Array.isArray(ids) ? ids : []).map((id) => normalizeEntityId(dimension, id));
